/*
 * Jira Ticket:
 * Created Date: Fri, 17th Jan 2020, 16:08:49 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import { ApolloClient } from 'apollo-client';
import { createHttpLink } from 'apollo-link-http';
import { onError } from 'apollo-link-error';
import { setContext } from 'apollo-link-context';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
  defaultDataIdFromObject,
} from 'apollo-cache-inmemory';
import schemaFragments from './fragmentTypes.json';
import getToken from 'utils/getToken';

const httpLink = uri =>
  createHttpLink({
    uri,
  });

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors && process.env.NODE_ENV === 'development')
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(`[GraphQL error]: Message: ${message}`);
    });
  if (networkError && process.env.NODE_ENV === 'development')
    console.error(`[Network error]: ${networkError}`);
});

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: schemaFragments,
});

const authLinkWithToken = setContext(async (meta, { headers }) => {
  const { operationName } = meta;
  if (
    operationName === 'termsAndConditions' ||
    operationName === 'createUser'
  ) {
    // public request
    return {
      headers: {
        ...headers,
      },
    };
  }
  // authenticated request
  const token = await getToken();
  return {
    headers: {
      ...headers,
      Authorization: token,
    },
  };
});

const apolloClient = (options = {}) => {
  const {
    clientOptions = {},
    inMemCacheOptions = {},
    httpLinkUri = process.env.REACT_APP_GRAPHQL_URI,
  } = options;

  return new ApolloClient({
    link: authLinkWithToken.concat(errorLink.concat(httpLink(httpLinkUri))),
    cache: new InMemoryCache({
      fragmentMatcher,
      dataIdFromObject: object => {
        switch (object.__typename) {
          case 'OrganisationCapability': // create an object for each with its value (true/false) - disable normalization not available in apollo-client v2.6
            return `${object.__typename}:${object.id}:${object.enabled}`;
          default:
            return defaultDataIdFromObject(object); // fall back to default handling
        }
      },
      ...inMemCacheOptions,
    }),
    defaultOptions: {
      query: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
    },
    ...clientOptions,
  });
};

export default apolloClient;
